import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useDonationCartEntries } from '@fhs/cart/src/queries/use-donation-cart-entries';
import { ISanityImage } from '@rbi-ctg/menu';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import useDonation from 'state/order/hooks/use-donation';
import { useUIContext } from 'state/ui';

import DonationAtCheckout from '../donation-at-checkout/donation-at-checkout';
import { IDonationAtCheckoutWithContentProps } from '../donation-at-checkout/types';
import { DonationsModal } from '../donations-modal';

import { DonationContainerSkeleton } from './donation-container-skeleton';

type DonationContainerProps = {
  isBadContinueAttempt: boolean;
  setIsBadContinueAttempt: React.Dispatch<React.SetStateAction<boolean>>;
};

const DonationContainer = ({
  isBadContinueAttempt,
  setIsBadContinueAttempt,
}: DonationContainerProps) => {
  const { formatMessage } = useIntl();
  const { featureDonation, featureDonationLoading } = useFeatureDonation();
  const { formatCurrencyForLocale } = useUIContext();

  const { setIsDonationZeroAmountSelected, selectedDonationId, setSelectedDonationId } =
    useOrderContext();
  const isExplicitDonation = useFlag(LaunchDarklyFlag.EXPLICIT_DONATION);

  const donationCartEntriesQuery = useDonationCartEntries();
  const donationItems = useMemo(
    () => donationCartEntriesQuery.data ?? [],
    [donationCartEntriesQuery.data]
  );

  const { handleAddDonationCartEntry, handleRemoveDonationCartEntry, selectedDonationCartEntry } =
    useDonation({
      donationItems,
    });

  const ZERO_AMOUNT_DONATION = '0';
  const [showDonationsInfoModal, setShowDonationsInfoModal] = useState(false);

  const allDonationOptions: IDonationAtCheckoutWithContentProps['donationOptions'] = useMemo(
    () => [
      {
        label: formatMessage({ id: 'none' }),
        value: ZERO_AMOUNT_DONATION,
      },
      ...(donationItems.map(item => ({
        label: formatCurrencyForLocale(item.price!),
        value: item.sanityId,
        isDisabled: false,
      })) as IDonationAtCheckoutWithContentProps['donationOptions']),
    ],
    [donationItems, formatCurrencyForLocale, formatMessage]
  );

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId?: string) => {
      setIsBadContinueAttempt(false);

      // user removed donation, remove previous donation cart entry
      if (ZERO_AMOUNT_DONATION === newSelectedDonationId) {
        setSelectedDonationId(ZERO_AMOUNT_DONATION);
        if (selectedDonationCartEntry) {
          handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        }
        setIsDonationZeroAmountSelected(true);
        return;
      }
      const optionItem = donationItems.find(item => item?.sanityId === newSelectedDonationId);
      if (!optionItem) {
        setSelectedDonationId('');
        return;
      }
      setSelectedDonationId(optionItem?.sanityId);
      setIsDonationZeroAmountSelected(false);
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
      setIsDonationZeroAmountSelected,
      setIsBadContinueAttempt,
      setSelectedDonationId,
    ]
  );

  const handleInfoOnPress = () => {
    setShowDonationsInfoModal(true);
  };

  const handleInfoOnDismiss = () => {
    setShowDonationsInfoModal(false);
  };

  if (featureDonationLoading || donationCartEntriesQuery.isLoading) {
    return <DonationContainerSkeleton />;
  }

  if (!donationCartEntriesQuery.data?.length) {
    return null;
  }

  return (
    <>
      {showDonationsInfoModal && (
        <DonationsModal
          isOpen={showDonationsInfoModal}
          onCloseCallback={handleInfoOnDismiss}
          title={featureDonation?.dialogInformation?.header?.locale ?? ''}
          modalAppearanceEventMessage="Donations Modal"
          buttonText={featureDonation?.dialogInformation?.buttonAction?.actionText?.locale ?? ''}
        >
          {featureDonation?.dialogInformation?.subtitle?.locale ?? ''}
        </DonationsModal>
      )}
      <DonationAtCheckout
        header={featureDonation?.header?.locale ?? ''}
        subtitle={featureDonation?.subtitle?.locale ?? ''}
        donationOptions={allDonationOptions}
        selectedDonationId={selectedDonationId}
        onChangeValue={handleDonationToggleChange}
        onInfoPressed={handleInfoOnPress}
        logo={featureDonation?.logoImage?.locale as ISanityImage}
        isContinueBlocked={isBadContinueAttempt && isExplicitDonation}
      />
    </>
  );
};

export default DonationContainer;
